import { useMemo, useState, useCallback } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import { ColumnConfig, FilterConfig, TableState } from "./types";

interface UseTableStateOptions<T> {
  data: T[];
  columns: ColumnConfig<T>[];
  filters?: FilterConfig[];
  itemsPerPage: number;
  debounceMs?: number;
}

/**
 * Hook que encapsula toda a lógica de estado da tabela:
 * busca, filtros, paginação e dados derivados.
 */
export function useTableState<T>({
  data,
  columns,
  filters,
  itemsPerPage,
  debounceMs = 300,
}: UseTableStateOptions<T>): TableState<T> {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);

  // Debounce da busca para melhor UX
  const debouncedSearch = useDebounce(searchTerm, debounceMs);

  // Colunas pesquisáveis
  const searchableKeys = useMemo(() => {
    return columns
      .filter((col) => col.searchable !== false)
      .map((col) => col.key);
  }, [columns]);

  // Aplicar filtros e busca
  const filteredData = useMemo(() => {
    let result = [...data];

    // Aplicar busca textual
    if (debouncedSearch.trim()) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter((row) =>
        searchableKeys.some((key) => {
          const value = row[key as keyof T];
          if (value == null) return false;
          return String(value).toLowerCase().includes(searchLower);
        })
      );
    }

    // Aplicar filtros ativos
    if (filters && Object.keys(activeFilters).length > 0) {
      result = result.filter((row) => {
        return Object.entries(activeFilters).every(([key, filterValue]) => {
          if (!filterValue) return true;
          const rowValue = row[key as keyof T];
          if (rowValue == null) return false;
          return String(rowValue).toLowerCase() === filterValue.toLowerCase();
        });
      });
    }

    return result;
  }, [data, debouncedSearch, activeFilters, searchableKeys, filters]);

  // Reset página quando filtros mudam
  useMemo(() => {
    setCurrentPage(1);
  }, [debouncedSearch, activeFilters]);

  // Calcular total de páginas
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  }, [filteredData.length, itemsPerPage]);

  // Dados paginados
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredData.slice(start, end);
  }, [filteredData, currentPage, itemsPerPage]);

  // Callbacks
  const setFilter = useCallback((key: string, value: string) => {
    setActiveFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setActiveFilters({});
    setSearchTerm("");
  }, []);

  return {
    searchTerm,
    setSearchTerm,
    activeFilters,
    setFilter,
    clearFilters,
    currentPage,
    setCurrentPage,
    filteredData,
    paginatedData,
    totalPages,
  };
}
